import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher, customFetcherRaw } from './customFetcher';
import { EVENT_RSVP_QUERY_KEY } from './eventInteractions';
import {
    IRegistrationFormResponse,
    IRegistrationFormWrite,
    IRegistrationOverview,
    registrationFormResponseSchema,
    registrationFormWriteSchema,
    registrationOverviewSchema,
} from '../Interfaces/IEventRegistration';

export const EVENT_REGISTRATION_FORM_QUERY_KEY = (eventId: number): (string | number)[] => [
    'eventRegistrationForm',
    eventId,
];

export const EVENT_REGISTRATION_OVERVIEW_QUERY_KEY = (eventId: number): (string | number)[] => [
    'eventRegistrationOverview',
    eventId,
];

/** Struktur der Zusatzanmeldung -- fuer jeden Eingeloggten lesbar (zum Ausfuellen). */
export const useEventRegistrationForm = (eventId?: number) => {
    const { data, isLoading, isError } = useQuery<IRegistrationFormResponse>({
        queryKey: EVENT_REGISTRATION_FORM_QUERY_KEY(eventId ?? 0),
        queryFn: async () => {
            const fetched = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${eventId}/registration-form/`,
            });
            return registrationFormResponseSchema.parse(fetched);
        },
        enabled: Boolean(eventId && eventId > 0),
        refetchOnWindowFocus: false,
    });

    return {
        registrationForm: data?.form ?? null,
        registrationAnswerCount: data?.answer_count ?? 0,
        isLoadingRegistrationForm: isLoading,
        isErrorRegistrationForm: isError,
    };
};

export interface SaveRegistrationFormInput {
    eventId: number;
    payload: IRegistrationFormWrite;
}

/**
 * Struktur speichern. Ein 409 (`code: "answers_exist"`) wird als Fehler geworfen und
 * vom Aufrufer ueber `parseBackendError` ausgewertet, um die Warnung aus Spec §14 zu
 * zeigen und danach mit `confirm_discard_answers: true` erneut zu senden.
 */
export const useSaveEventRegistrationForm = () => {
    const queryClient = useQueryClient();

    return useMutation<IRegistrationFormResponse, Error, SaveRegistrationFormInput>({
        mutationFn: async ({ eventId, payload }) => {
            const body = registrationFormWriteSchema.parse(payload);
            const response = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${eventId}/registration-form/`,
                method: 'PUT',
                body,
            });
            return registrationFormResponseSchema.parse(response);
        },
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({
                queryKey: EVENT_REGISTRATION_FORM_QUERY_KEY(variables.eventId),
            });
            void queryClient.invalidateQueries({
                queryKey: EVENT_REGISTRATION_OVERVIEW_QUERY_KEY(variables.eventId),
            });
            void queryClient.invalidateQueries({
                queryKey: EVENT_RSVP_QUERY_KEY(variables.eventId),
            });
        },
    });
};

export const useDeleteEventRegistrationForm = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: async (eventId) => {
            await customFetcher({
                endpoint: `/calendar-entries/${eventId}/registration-form/`,
                method: 'DELETE',
            });
        },
        onSuccess: (_data, eventId) => {
            void queryClient.invalidateQueries({
                queryKey: EVENT_REGISTRATION_FORM_QUERY_KEY(eventId),
            });
            void queryClient.invalidateQueries({
                queryKey: EVENT_REGISTRATION_OVERVIEW_QUERY_KEY(eventId),
            });
            void queryClient.invalidateQueries({
                queryKey: EVENT_RSVP_QUERY_KEY(eventId),
            });
        },
    });
};

/** Verwaltungsansicht -- serverseitig auf Gruppe 1/2 beschraenkt. */
export const useEventRegistrationOverview = (eventId?: number, enabled = true) => {
    const { data, isLoading, isError, refetch } = useQuery<IRegistrationOverview | null>({
        queryKey: EVENT_REGISTRATION_OVERVIEW_QUERY_KEY(eventId ?? 0),
        queryFn: async () => {
            const fetched = await customFetcher<unknown>({
                endpoint: `/calendar-entries/${eventId}/registrations/`,
            });
            return registrationOverviewSchema.parse(fetched);
        },
        enabled: Boolean(enabled && eventId && eventId > 0),
        refetchOnWindowFocus: false,
    });

    return {
        registrationOverview: data,
        isLoadingRegistrationOverview: isLoading,
        isErrorRegistrationOverview: isError,
        refetchRegistrationOverview: refetch,
    };
};

/** Loest den Browser-Download des CSV-Exports aus (Vorbild: `downloadKurtaxeExport`). */
export const downloadEventRegistrationsExport = async (
    eventId: number,
    eventTitle: string
): Promise<void> => {
    const response = await customFetcherRaw({
        endpoint: `/calendar-entries/${eventId}/registrations/export/`,
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const slug = (eventTitle || 'Veranstaltung').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');
    link.href = url;
    link.download = `Anmeldungen_${slug}_${eventId}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
